
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, TrendingUp, Calendar, Award, Brain, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

const raceDistances = [
  { value: 5000, label: '5K', pace: 'aggressive' },
  { value: 10000, label: '10K', pace: 'threshold' },
  { value: 21097, label: 'Half Marathon', pace: 'tempo' },
  { value: 42195, label: 'Marathon', pace: 'aerobic' }
];

export const PerformanceForecastingDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedDistance, setSelectedDistance] = useState(10000);
  const [forecastWeeks, setForecastWeeks] = useState(8);

  // Fetch race prediction
  const { data: raceprediction } = useQuery({
    queryKey: ['race-prediction', user?.id, selectedDistance],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase.rpc('predict_race_time', {
        p_user_id: user.id,
        p_target_distance: selectedDistance,
        p_weeks_history: 12
      });
      
      if (error) throw error;
      return data[0];
    },
    enabled: !!user,
  });

  // Fetch performance trajectory
  const { data: trajectoryData } = useQuery({
    queryKey: ['performance-trajectory', user?.id, forecastWeeks],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase.rpc('forecast_performance_trajectory', {
        p_user_id: user.id,
        p_forecast_weeks: forecastWeeks
      });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Format time for display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (totalSeconds: number, distanceMeters: number) => {
    const pacePerKm = totalSeconds / (distanceMeters / 1000);
    const minutes = Math.floor(pacePerKm / 60);
    const seconds = Math.round(pacePerKm % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  const selectedRace = raceDistances.find(r => r.value === selectedDistance);

  return (
    <div className="space-y-6">
      {/* Race Prediction Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Race Time Prediction
          </CardTitle>
          <CardDescription>
            AI-powered race time predictions based on your training data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Race Distance</label>
              <Select value={selectedDistance.toString()} onValueChange={(value) => setSelectedDistance(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {raceDistances.map((race) => (
                    <SelectItem key={race.value} value={race.value.toString()}>
                      {race.label} ({(race.value / 1000).toFixed(1)}km)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Forecast Period</label>
              <Select value={forecastWeeks.toString()} onValueChange={(value) => setForecastWeeks(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 weeks</SelectItem>
                  <SelectItem value="8">8 weeks</SelectItem>
                  <SelectItem value="12">12 weeks</SelectItem>
                  <SelectItem value="16">16 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {raceprediction && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Predicted Time</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {formatTime(raceprediction.predicted_time_seconds)}
                  </div>
                  <div className="text-sm text-blue-700 mt-1">
                    {formatPace(raceprediction.predicted_time_seconds, selectedDistance)} pace
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Confidence</span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {Math.round(raceprediction.confidence_score)}%
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 mt-1">
                    {raceprediction.confidence_score > 80 ? 'High' : 
                     raceprediction.confidence_score > 60 ? 'Medium' : 'Low'} Confidence
                  </Badge>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Goal Zone</span>
                  </div>
                  <div className="text-lg font-bold text-purple-900">
                    {selectedRace?.pace.toUpperCase()}
                  </div>
                  <div className="text-sm text-purple-700 mt-1">
                    Recommended training focus
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {raceprediction?.training_recommendation && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-1">Training Recommendation</h4>
              <p className="text-sm text-gray-700">{raceprediction.training_recommendation}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Trajectory Chart */}
      {trajectoryData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Performance Trajectory ({forecastWeeks} weeks)
            </CardTitle>
            <CardDescription>
              Projected fitness, fatigue, and performance potential based on current training patterns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trajectoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="week_number" 
                    tickFormatter={(week) => `Week ${week}`}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      value.toFixed(1),
                      name === 'predicted_fitness' ? 'Fitness Score' :
                      name === 'predicted_fatigue' ? 'Fatigue Score' :
                      'Performance Potential'
                    ]}
                    labelFormatter={(week) => `Week ${week}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="predicted_fitness" 
                    stackId="1" 
                    stroke="#10b981" 
                    fill="#10b981" 
                    fillOpacity={0.3}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="predicted_fatigue" 
                    stackId="2" 
                    stroke="#ef4444" 
                    fill="#ef4444" 
                    fillOpacity={0.3}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="performance_potential" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Training Focus */}
      {trajectoryData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Recommended Training Focus
            </CardTitle>
            <CardDescription>
              Week-by-week training emphasis based on predicted performance trajectory
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {trajectoryData.slice(0, 8).map((week) => (
                <Card key={week.week_number} className="border border-gray-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Week {week.week_number}</span>
                      <Badge 
                        variant="secondary"
                        className={`${
                          week.performance_potential > 15 ? 'bg-green-100 text-green-800' :
                          week.performance_potential > 5 ? 'bg-blue-100 text-blue-800' :
                          week.performance_potential > -5 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}
                      >
                        {week.performance_potential > 15 ? 'Peak' :
                         week.performance_potential > 5 ? 'Build' :
                         week.performance_potential > -5 ? 'Recovery' : 'Rest'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600">{week.training_focus}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      Potential: {week.performance_potential.toFixed(1)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
